import type { Renderer } from '../base/Renderer';
import type { SimulationState, Coordinate } from '../../core/types/SimulationTypes';

export class TopDownRenderer implements Renderer {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private container: HTMLElement | null = null;
  private cellSize: number = 8;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private gridWidth: number = 0;
  private gridHeight: number = 0;

  public initialize(container: HTMLElement): void {
    this.container = container;

    this.canvas = document.createElement('canvas');
    this.canvas.style.imageRendering = 'pixelated';
    this.canvas.style.imageRendering = 'crisp-edges';

    this.ctx = this.canvas.getContext('2d', { alpha: false });
    if (!this.ctx) {
      throw new Error('Failed to get 2D rendering context');
    }

    container.appendChild(this.canvas);

    this.resize(container.clientWidth, container.clientHeight);

    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    if (this.container) {
      this.resize(this.container.clientWidth, this.container.clientHeight);
    }
  };

  public resize(width: number, height: number): void {
    if (!this.canvas) return;

    this.canvas.width = width;
    this.canvas.height = height;
  }

  public render(state: SimulationState): void {
    if (!this.ctx || !this.canvas) return;

    const { sandGrid, waterGrid, metadata } = state;
    const { gridWidth, gridHeight, maxHeight } = metadata;

    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;

    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;

    this.cellSize = Math.min(
      Math.floor(canvasWidth / gridWidth),
      Math.floor(canvasHeight / gridHeight)
    );

    this.offsetX = (canvasWidth - gridWidth * this.cellSize) / 2;
    this.offsetY = (canvasHeight - gridHeight * this.cellSize) / 2;

    this.ctx.fillStyle = '#2a2a2a';
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const height = sandGrid.get(x, y);
        const waterVolume = waterGrid?.getHeight(x, y) || 0;

        const normalizedHeight = maxHeight > 0 ? height / maxHeight : 0;

        const sandR = 232 + (196 - 232) * normalizedHeight;
        const sandG = 212 + (165 - 212) * normalizedHeight;
        const sandB = 160 + (114 - 160) * normalizedHeight;

        this.ctx.fillStyle = `rgb(${sandR}, ${sandG}, ${sandB})`;
        this.ctx.fillRect(
          this.offsetX + x * this.cellSize,
          this.offsetY + y * this.cellSize,
          this.cellSize,
          this.cellSize
        );

        if (waterVolume > 0) {
          const waterAlpha = Math.min(waterVolume / 5, 0.8);
          const waterDepth = Math.min(waterVolume / 10, 1);

          const waterR = 74 + (30 - 74) * waterDepth;
          const waterG = 158 + (90 - 158) * waterDepth;
          const waterB = 219 + (142 - 219) * waterDepth;

          this.ctx.fillStyle = `rgba(${waterR}, ${waterG}, ${waterB}, ${waterAlpha})`;
          this.ctx.fillRect(
            this.offsetX + x * this.cellSize,
            this.offsetY + y * this.cellSize,
            this.cellSize,
            this.cellSize
          );
        }
      }
    }
  }

  public screenToWorld(screenX: number, screenY: number): Coordinate | null {
    if (!this.canvas || !this.container) return null;

    // Recalculate offsets if grid dimensions are known but offsets haven't been set
    if (this.gridWidth > 0 && this.gridHeight > 0) {
      const canvasWidth = this.canvas.width;
      const canvasHeight = this.canvas.height;

      this.cellSize = Math.min(
        Math.floor(canvasWidth / this.gridWidth),
        Math.floor(canvasHeight / this.gridHeight)
      );

      this.offsetX = (canvasWidth - this.gridWidth * this.cellSize) / 2;
      this.offsetY = (canvasHeight - this.gridHeight * this.cellSize) / 2;
    }

    const rect = this.canvas.getBoundingClientRect();
    const canvasX = screenX - rect.left;
    const canvasY = screenY - rect.top;

    const worldX = Math.floor((canvasX - this.offsetX) / this.cellSize);
    const worldY = Math.floor((canvasY - this.offsetY) / this.cellSize);

    return { x: worldX, y: worldY };
  }

  public pan(deltaX: number, deltaY: number): void {
    // Top-down view doesn't support panning
  }

  public zoom(delta: number): void {
    // Top-down view doesn't support zooming
  }

  public drawHoverPreview(worldCoord: Coordinate, brushSize: number): void {
    if (!this.ctx || !this.canvas) return;

    // Draw brush preview circle
    const centerX = this.offsetX + (worldCoord.x + 0.5) * this.cellSize;
    const centerY = this.offsetY + (worldCoord.y + 0.5) * this.cellSize;
    const radius = brushSize * this.cellSize;

    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    this.ctx.stroke();

    // Draw center crosshair
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    this.ctx.lineWidth = 1;
    this.ctx.beginPath();
    this.ctx.moveTo(centerX - 5, centerY);
    this.ctx.lineTo(centerX + 5, centerY);
    this.ctx.moveTo(centerX, centerY - 5);
    this.ctx.lineTo(centerX, centerY + 5);
    this.ctx.stroke();
  }

  public dispose(): void {
    window.removeEventListener('resize', this.handleResize);

    if (this.canvas && this.container) {
      this.container.removeChild(this.canvas);
    }

    this.canvas = null;
    this.ctx = null;
    this.container = null;
  }
}
